import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import {
  ArdoqError,
  getArdoqErrorTraceId,
  isArdoqError,
} from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { confirm } from '@ardoq/modal';
import { I18nLocale, t } from '@ardoq/i18n';
import { CellValueChangedEvent, GridApi, IRowNode } from 'ag-grid-enterprise';
import { groupBy, isEqual, uniq } from 'lodash';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import {
  BulkEditEventSource,
  ColumnDefinitionArdoqMetadata,
  ExtendedRowNode,
  InventoryContext,
  InventoryReferenceRepresentation,
  MetamodelWithTriplesWithOrigin,
  PendingReferenceChange,
  ReferenceCreationRequest,
  ReferenceUpdateSource,
} from '../types';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import { mergeScopeDataIntoInventoryContext } from '../mergeScopeDataIntoInventoryContext';
import { toInventoryReferenceRepresentation } from '../toInventoryReferenceRepresentation';
import { dateRangeOperations } from '@ardoq/date-range';
import {
  getBulkReferenceDeleteConfirmationDialog,
  getNothingChangedDialog,
} from './confirmDialogs';

export const showEditReferencesFailedDialog = async (
  i18nLocale: I18nLocale,
  result: ArdoqError
): Promise<void> => {
  const traceId = getArdoqErrorTraceId(result);
  await confirm({
    title: t(
      i18nLocale,
      'inventory:editReferencesFailed.title',
      'Editing references failed'
    ),
    text: t(
      i18nLocale,
      'inventory:editReferencesFailed.text',
      'Your changes could not be saved and will be reverted.'
    ),
    text2:
      traceId &&
      t(
        i18nLocale,
        'inventory:editReferencesFailed.traceId',
        { traceId },
        `Trace ID: ${traceId}`
      ),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  });
};

type ReferenceCellValueChangedEvent = CellValueChangedEvent<
  APIComponentAttributes,
  InventoryReferenceRepresentation[],
  InventoryContext
>;

/**
 * What a cell going from one set of references to another has to create and
 * delete. Returns `null` when the cell did not actually change.
 */
export const toPendingReferenceChangeFromValues = ({
  componentId,
  referenceTypeName,
  oldReferences,
  newReferences,
}: {
  componentId: ArdoqId;
  referenceTypeName: string;
  oldReferences: InventoryReferenceRepresentation[];
  newReferences: InventoryReferenceRepresentation[];
}): PendingReferenceChange | null => {
  if (isEqual(oldReferences, newReferences)) {
    return null;
  }
  return {
    componentId,
    referenceTypeName,
    referencesToDelete: differenceWithDuplicates(oldReferences, newReferences),
    referencesToCreate: differenceWithDuplicates(newReferences, oldReferences),
  };
};

/**
 * Returns `null` when the cell did not actually change.
 */
export const toPendingReferenceChange = (
  event: ReferenceCellValueChangedEvent
): PendingReferenceChange | null => {
  const columnContext = event.colDef.context as ColumnDefinitionArdoqMetadata;
  const componentId = event.data?._id;
  if (!componentId || columnContext.inventoryColumnType !== 'reference') {
    return null;
  }

  return toPendingReferenceChangeFromValues({
    componentId,
    referenceTypeName: columnContext.referenceTypeName,
    oldReferences: event.oldValue ?? [],
    newReferences: event.newValue ?? [],
  });
};

const AG_GRID_CELL_CLEAR_SOURCE = 'cellClear';

const toSingleComponentUpdateSource = (
  event: ReferenceCellValueChangedEvent
): ReferenceUpdateSource =>
  event.source === AG_GRID_CELL_CLEAR_SOURCE ? 'delete' : 'cell-editor';

/**
 * Returns `false` when the edit was reverted (the user cancelled a bulk delete).
 */
export const editReferencesSingleComponent = async (
  event: ReferenceCellValueChangedEvent
): Promise<boolean> => {
  const pendingChange = toPendingReferenceChange(event);
  if (!pendingChange) {
    return true;
  }

  const { i18nLocale } = event.context;
  const scopeData = await loadScopeDataForPendingReferences(
    [pendingChange],
    event.api,
    event.context
  );
  const resolvedChanges = resolveReferenceChanges(
    [pendingChange],
    { ...event.context, scopeData },
    // the cell editor only offers component types the metamodel already allows,
    // so checking again here would re-reject a pick the UI accepted
    { enforceTriples: false }
  );
  const { referencesToCreate, referenceIdsToDelete, blockedDeleteCount } =
    resolvedChanges;

  if (referencesToCreate.length === 0 && referenceIdsToDelete.length === 0) {
    const { skippedNoTripleCount, unloadedComponentCount, blockedCreateCount } =
      resolvedChanges;
    if (
      blockedDeleteCount > 0 ||
      skippedNoTripleCount > 0 ||
      unloadedComponentCount > 0 ||
      blockedCreateCount > 0
    ) {
      await confirm(
        getNothingChangedDialog({
          blockedDeleteCount,
          skippedNoTripleCount,
          unloadedComponentCount,
          blockedCreateCount,
          i18nLocale,
        })
      );
    }
    return false; // nothing was saved, so the cell has to go back
  }

  if (referenceIdsToDelete.length > 0 && event.newValue === null) {
    // this means the user cleared the cell by pressing delete or backspace
    const confirmed = await confirm(
      getBulkReferenceDeleteConfirmationDialog({
        createCount: referencesToCreate.length,
        deleteCount: referenceIdsToDelete.length,
        componentCount: resolvedChanges.componentCount,
        blockedDeleteCount,
        fieldUpdateComponentCount: 0,
        i18nLocale,
      })
    );
    if (!confirmed) {
      return false;
    }
  }

  await sendReferenceChanges(
    resolvedChanges,
    event.context,
    toSingleComponentUpdateSource(event)
  );

  return true;
};

const sendReferenceChanges = async (
  { referencesToCreate, referenceIdsToDelete }: ResolvedReferenceChanges,
  { commands, i18nLocale }: Pick<InventoryContext, 'commands' | 'i18nLocale'>,
  source: ReferenceUpdateSource
): Promise<void> => {
  const result = await commands.bulkUpdateReferences({
    referencesToDelete: referenceIdsToDelete.length
      ? referenceIdsToDelete
      : undefined,
    referencesToCreate: referencesToCreate.length
      ? referencesToCreate
      : undefined,
    source,
  });

  if (isArdoqError(result)) {
    await showEditReferencesFailedDialog(i18nLocale, result);
  }
};

export type ResolvedReferenceChanges = {
  referencesToCreate: ReferenceCreationRequest[];
  referenceIdsToDelete: ArdoqId[];
  /** References the user may not edit. Excluded from the request. */
  blockedDeleteCount: number;
  /** Creates the metamodel has no triple for. Excluded from the request. */
  skippedNoTripleCount: number;
  /**
   * Creates whose referenced component could not be loaded, so nothing about
   * them could be checked. Excluded from the request.
   */
  unloadedComponentCount: number;
  /** Creates whose source component the user may not edit. Excluded. */
  blockedCreateCount: number;
  /** Components whose references actually change. */
  componentCount: number;
  /** Whether anything was pending, even if all of it was filtered out. */
  hasPendingChanges: boolean;
};

const toReferenceKey = ({
  source,
  target,
  type,
}: {
  source: ArdoqId;
  target: ArdoqId;
  type: number | undefined;
}) => `${source}\0${target}\0${type}`;

/**
 * Buckets only the references some pending change asks to remove, so a range
 * clear resolves in one pass while a single-cell edit stays proportional to
 * what it touches. Duplicates between the same pair queue under one key and
 * are consumed in turn.
 */
const indexRequestedReferences = (
  pendingChanges: PendingReferenceChange[],
  references: APIReferenceAttributes[]
) => {
  const requestedKeys = new Set(
    pendingChanges.flatMap(({ referencesToDelete }) =>
      referencesToDelete.map(toReferenceKey)
    )
  );
  return groupBy(
    references.filter(reference =>
      requestedKeys.has(toReferenceKey(reference))
    ),
    toReferenceKey
  );
};

const getComponentTypeName = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): string | null => {
  const component = scopeData.componentsById[componentId];
  if (!component) {
    return null;
  }
  return (
    enhancedScopeDataOperations.getComponentTypeByModelIdAndTypeId(
      scopeData,
      component.model,
      component.typeId
    )?.name ?? null
  );
};

/**
 * The backend does not validate triples, so for a paste this is the only check.
 * A type that is still unknown after the components have been fetched cannot be
 * checked at all, so it is refused.
 */
const toTripleKey = (
  sourceComponentType: string,
  referenceType: string,
  targetComponentType: string
) => `${sourceComponentType}\0${referenceType}\0${targetComponentType}`;

const indexTriples = (metaModel: MetamodelWithTriplesWithOrigin) =>
  new Set(
    metaModel.triples.map(triple =>
      toTripleKey(
        triple.sourceComponentType,
        triple.referenceType,
        triple.targetComponentType
      )
    )
  );

const isTripleAllowed = (
  allowedTriples: Set<string>,
  scopeData: EnhancedScopeData,
  { source, target }: ReferenceCreationRequest,
  referenceTypeName: string
): boolean => {
  const sourceComponentType = getComponentTypeName(scopeData, source);
  const targetComponentType = getComponentTypeName(scopeData, target);
  if (!sourceComponentType || !targetComponentType) {
    return false;
  }
  return allowedTriples.has(
    toTripleKey(sourceComponentType, referenceTypeName, targetComponentType)
  );
};

/**
 * `enforceTriples` is for pastes, which can pair any two components. The cell
 * editor only offers component types the metamodel already allows, so checking
 * again there would just re-reject picks the UI had accepted.
 */
const toReferenceCreationRequest = (
  reference: InventoryReferenceRepresentation,
  scopeData: EnhancedScopeData,
  referenceTypeName: string
): ReferenceCreationRequest | null => {
  const { source, target, type, direction } = reference;
  if (type !== undefined) {
    return { source, target, type };
  }

  const isOutgoing = direction === 'outgoing';
  const editedComponent =
    scopeData.componentsById[isOutgoing ? source : target];
  if (!editedComponent) {
    return null;
  }

  const { type: resolvedType } = toInventoryReferenceRepresentation({
    scopeData,
    editedComponent,
    referencedComponentId: isOutgoing ? target : source,
    direction,
    referenceTypeName,
  });
  return resolvedType === undefined
    ? null
    : { source, target, type: resolvedType };
};

/**
 * Whether both ends of the reference are in scope data. One is not when the
 * fetch for it failed or it no longer exists, and in neither case did the
 * metamodel get a say in the refusal.
 */
const areBothEndsLoaded = (
  {
    source,
    target,
  }: ReferenceCreationRequest | InventoryReferenceRepresentation,
  scopeData: EnhancedScopeData
): boolean =>
  Boolean(scopeData.componentsById[source] && scopeData.componentsById[target]);

const toComponentIdsNeedingScopeData = (
  pendingChanges: PendingReferenceChange[],
  scopeData: EnhancedScopeData
): ArdoqId[] =>
  uniq(
    pendingChanges
      .flatMap(({ referencesToCreate }) => referencesToCreate)
      .filter(({ type }) => type === undefined)
      .map(({ source }) => source)
      .filter(componentId => !scopeData.componentsById[componentId])
  );

/**
 * Loads the components a paste needs but the grid has not seen, so the model
 * that owns each reference can name its type. Goes through the shared merge so
 * the scope-data date-range invariant holds.
 */
export const loadScopeDataForPendingReferences = async (
  pendingChanges: PendingReferenceChange[],
  api: GridApi<APIComponentAttributes>,
  context: Pick<InventoryContext, 'scopeData' | 'commands'>
): Promise<EnhancedScopeData> => {
  const componentIds = toComponentIdsNeedingScopeData(
    pendingChanges,
    context.scopeData
  );
  if (componentIds.length === 0) {
    return context.scopeData;
  }
  const fetched =
    await context.commands.fetchScopeDataForComponents(componentIds);
  if (isArdoqError(fetched)) {
    return context.scopeData;
  }
  mergeScopeDataIntoInventoryContext(
    api,
    dateRangeOperations.getMergedDateRangeFieldForScopeData(fetched)
  );
  return (api.getGridOption('context') as InventoryContext).scopeData;
};

export const resolveReferenceChanges = (
  pendingChanges: PendingReferenceChange[],
  context: Pick<InventoryContext, 'scopeData' | 'metaModel' | 'accessControl'>,
  { enforceTriples }: { enforceTriples: boolean }
): ResolvedReferenceChanges => {
  const { scopeData, metaModel, accessControl } = context;
  const { canEditReference } = accessControl;
  let skippedNoTripleCount = 0;
  let unloadedComponentCount = 0;
  let blockedCreateCount = 0;
  let blockedDeleteCount = 0;
  const referenceIdsToDelete: ArdoqId[] = [];
  const changedComponentIds = new Set<ArdoqId>();
  const referencesToCreate: ReferenceCreationRequest[] = [];

  const allowedTriples = enforceTriples
    ? indexTriples(metaModel)
    : new Set<string>();
  const referencesByKey = indexRequestedReferences(
    pendingChanges,
    scopeData.references
  );

  const countRefusedCreate = (
    create: ReferenceCreationRequest | InventoryReferenceRepresentation
  ) => {
    if (areBothEndsLoaded(create, scopeData)) {
      skippedNoTripleCount += 1;
      return;
    }
    unloadedComponentCount += 1;
  };

  pendingChanges.forEach(
    ({
      componentId,
      referenceTypeName,
      referencesToCreate: pendingCreates,
      referencesToDelete,
    }) => {
      const survivingCreates = pendingCreates.flatMap(pendingCreate => {
        const reference = toReferenceCreationRequest(
          pendingCreate,
          scopeData,
          referenceTypeName
        );
        if (!reference) {
          countRefusedCreate(pendingCreate);
          return [];
        }
        if (
          enforceTriples &&
          !isTripleAllowed(
            allowedTriples,
            scopeData,
            reference,
            referenceTypeName
          )
        ) {
          countRefusedCreate(reference);
          return [];
        }
        const sourceComponent = scopeData.componentsById[reference.source];
        if (
          sourceComponent &&
          !accessControl.hasEditPermissionForComponent(sourceComponent)
        ) {
          blockedCreateCount += 1;
          return [];
        }
        return [reference];
      });

      if (pendingCreates.length > 0 && survivingCreates.length === 0) {
        return;
      }

      survivingCreates.forEach(reference => {
        referencesToCreate.push(reference);
        changedComponentIds.add(componentId);
      });
      referencesToDelete.forEach(removedReference => {
        const reference =
          referencesByKey[toReferenceKey(removedReference)]?.shift();
        if (!reference) {
          return;
        }
        if (!canEditReference(reference)) {
          blockedDeleteCount += 1;
          return;
        }
        referenceIdsToDelete.push(reference._id);
        changedComponentIds.add(componentId);
      });
    }
  );

  return {
    referencesToCreate,
    referenceIdsToDelete,
    blockedDeleteCount,
    skippedNoTripleCount,
    unloadedComponentCount,
    blockedCreateCount,
    componentCount: changedComponentIds.size,
    hasPendingChanges: pendingChanges.length > 0,
  };
};

export const collectPendingReferenceChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
): PendingReferenceChange[] => {
  const pendingReferenceChanges: PendingReferenceChange[] = [];
  const collect = (node?: IRowNode<APIComponentAttributes> | null) => {
    const changes = (node as ExtendedRowNode | undefined)
      ?._pendingReferenceChanges;
    if (changes) {
      pendingReferenceChanges.push(...changes);
    }
  };

  api.forEachNode(collect);

  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      collect(node);
    }
  }

  return pendingReferenceChanges;
};

export const clearPendingReferenceChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
) => {
  api.forEachNode(node => {
    (node as ExtendedRowNode)._pendingReferenceChanges = undefined;
  });
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (node) {
      (node as ExtendedRowNode)._pendingReferenceChanges = undefined;
    }
  }
};

/**
 * Returns `true` when the grid has to be refreshed, which is whenever anything
 * was pending: the cells were already updated optimistically, and only the
 * server knows which of those changes survived.
 */
export const multiUpdateReferences = async (
  resolvedChanges: ResolvedReferenceChanges,
  context: InventoryContext,
  api: GridApi<APIComponentAttributes>,
  eventSource: BulkEditEventSource
): Promise<boolean> => {
  const { referencesToCreate, referenceIdsToDelete, hasPendingChanges } =
    resolvedChanges;
  if (!hasPendingChanges) {
    return false;
  }

  if (referencesToCreate.length > 0 || referenceIdsToDelete.length > 0) {
    await sendReferenceChanges(resolvedChanges, context, eventSource);
  }

  clearPendingReferenceChangesFromNodes(api);
  return true;
};

const differenceWithDuplicates = <T>(array: T[], excludeArray: T[]): T[] => {
  const result = [...array];

  excludeArray.forEach(itemToRemove => {
    const index = result.findIndex(item => isEqual(item, itemToRemove));
    if (index !== -1) {
      result.splice(index, 1);
    }
  });

  return result;
};
